// ConnectWithUsBtns.jsx
import React from "react";


/**
 * Pure-CSS ConnectWithUsBtns
 * Props:
 * - links: { facebook?, youtube?, x?, connect? }  // each is a URL string
 * - size: "sm" | "md" | "lg" (default: "md")
 * - variant: "solid" | "outline" (default: "solid")
 *
 * Usage:
 * <ConnectWithUsBtns
 *   links={{ facebook: 'https://facebook.com/yourpage', youtube: 'https://youtube.com/yourchannel', x: 'https://x.com/yourhandle', connect: 'mailto:you@example.com' }}
 *   size="md"
 *   variant="solid"
 * />
 */

const ICONS = {
  facebook: (
    <svg viewBox="0 0 24 24" className="scb-icon" aria-hidden>
      <path d="M22 12.07C22 6.48 17.52 2 11.93 2S2 6.48 2 12.07C2 17.1 5.66 21.18 10.44 21.93v-6.4H7.9v-2.95h2.54V9.9c0-2.5 1.49-3.88 3.76-3.88 1.09 0 2.23.2 2.23.2v2.46h-1.26c-1.24 0-1.62.77-1.62 1.56v1.87h2.75l-.44 2.95h-2.31v6.4C18.34 21.18 22 17.1 22 12.07z" />
    </svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" className="scb-icon" aria-hidden>
      <path d="M10 15l5.2-3L10 9v6zm11.7-6.1s-.1-1-.4-1.4c-.4-.9-1.5-1-1.9-1.1C17.3 6.9 12 6.9 12 6.9h-.1S6.7 6.9 4.6 6.4c-.4.1-1.6.2-2 1.1-.3.4-.4 1.4-.4 1.4S2 10.1 2 11.2v1.6c0 1.1.1 2.3.1 2.3s.1 1 .4 1.4c.4.9 1.5 1 1.9 1.1 1.6.3 7.4.3 7.4.3s5.3 0 7.4-.3c.4-.1 1.6-.2 1.9-1.1.3-.4.4-1.4.4-1.4s.1-1.2.1-2.3v-1.6c0-1.1-.1-2.2-.1-2.2z" />
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" className="scb-icon" aria-hidden>
      <path d="M23 3.2c-.4-.3-.9-.2-1.3.1l-6.4 4.9L8.8 2.8C8.3 2.4 7.6 2.5 7.2 3c-.4.4-.5 1-.1 1.5l5.4 6.1-6.8 5c-.6.4-.8 1.2-.4 1.8.4.6 1.2.8 1.8.4l7.5-5.6 6 4.6c.4.3 1 .3 1.4 0 .4-.3.5-.9.2-1.3L17.8 12l6.9-5.6c.4-.3.5-1 .3-1.4z" />
    </svg>
  ),
  connect: (
    <svg viewBox="0 0 24 24" className="scb-icon" aria-hidden>
      <path d="M12 2a5 5 0 00-5 5v2H6a2 2 0 00-2 2v6a3 3 0 003 3h8a3 3 0 003-3v-6a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zM9 7a3 3 0 116 0v2H9V7z" />
    </svg>
  ),
};

const SIZE_CLASS = {
  sm: "scb-sm",
  md: "scb-md",
  lg: "scb-lg",
};

const ConnectWithUsBtns = ({ links = {}, size = "md", variant = "solid" }) => {
  const btnClass = variant === "outline" ? "scb-btn scb-outline" : "scb-btn scb-solid";
  const sizeClass = SIZE_CLASS[size] || SIZE_CLASS.sm;

  const makeBtn = (key, url) => {
    if (!url) return null;
    return (
      <a
        key={key}
        href={url}
        className={`${btnClass} ${sizeClass}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${key}`}
      >
        <span className="scb-icon-wrap">{ICONS[key]}</span>
        <span className="scb-label">{key === "x" ? "X" : key.charAt(0).toUpperCase() + key.slice(1)}</span>
      </a>
    );
  };

  return (<><div className="scb-wrapper">{[makeBtn("facebook", links.facebook), makeBtn("youtube", links.youtube), makeBtn("x", links.x), makeBtn("connect", links.connect)]}</div>
 <style>{ `
/*
 ConnectWithUsBtns.css
 Paste this file next to the component as ConnectWithUsBtns.css
*/

/* Base wrapper */
/* scb = social connect buttons */

/* Layout */
.scb-wrapper { width:300px;
  display: flex;justify-content:space-between !important;
  gap: 12px;
  align-items: center;
}

/* Button base */
.scb-btn {
  display: inline-flex;width:100%;
  align-items: center;max-width:100px;
  border-radius: 16px;
  text-decoration: none;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
  font-weight: 600;
  transition: transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  padding: 10px 14px;
}

.scb-btn:active { transform: scale(0.98); }
.scb-btn:focus { outline: 3px solid rgba(59,130,246,0.25); outline-offset: 2px; }

/* Icon wrapper */
.scb-icon-wrap { display: inline-flex; align-items: center; justify-content: center; margin-right: 8px; }
.scb-icon { width: 20px; height: 20px; fill: currentColor; display: block; }

/* Label */
.scb-label { white-space: nowrap; }

/* Variants */
.scb-solid {
  color: white;
  background: linear-gradient(90deg, #2563EB 0%, #6366F1 100%); /* blue -> indigo */
  border: none;
}

.scb-outline {
  color: #1F2937; /* gray-800 */
  background: white;
  border: 1px solid #E5E7EB; /* gray-200 */
}

/* Sizes */
.scb-sm { padding: 6px 10px; border-radius: 12px; font-size: 13px; }
.scb-md { padding: 10px 14px; border-radius: 16px; font-size: 15px; }
.scb-lg { padding: 14px 18px; border-radius: 20px; font-size: 17px; }

/* Hover states */
.scb-solid:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(99,102,241,0.12); }
.scb-outline:hover { background: #F9FAFB; transform: translateY(-2px); box-shadow: 0 6px 18px rgba(15,23,42,0.04); }

/* Responsive truncation */
@media (max-width: 420px) {
 
  .scb-btn { padding-left: 10px; padding-right: 10px; }
}

/* Utility for small icon-only square */
.scb-icon-only { padding: 8px; border-radius: 50%; }

/* Colour overrides for brand-specific look (optional) */
.scb-facebook.scb-solid { background: linear-gradient(90deg,#1877F2 0%, #166FE5 100%); }
.scb-youtube.scb-solid { background: linear-gradient(90deg,#FF0000 0%, #C71515 100%); }
.scb-x.scb-solid { background: linear-gradient(90deg,#1DA1F2 0%, #0B75C9 100%); }

/* Example utility classes if you want to use brand colours when rendering */

/* End of CSS */`}
</style></>
)
  
  
  ;
};

export default ConnectWithUsBtns;
 